import * as React from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import Backdrop from "@mui/material/Backdrop";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useEffect } from "react";
import { useState } from "react";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Modal,
  TablePagination,
  TextField,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import {
  buildSyncJobOrg,
  createCronJob,
  cronJobId,
  deleteCronJob,
  getModuleSettings,
  historicalJobs,
  isBackfillJob,
  listCronJobs,
} from "../../utils/api";
import { JOB_TYPES } from "../../utils/constants";
import { DELUGE_FUNCTIONS } from "../../utils/orgVariables";
import { SURFACE_OVERLAY } from "../../utils/colors";
import {
  FS_BODY,
  FS_HEADING,
  FS_TITLE,
  FW_MEDIUM,
  FW_SEMIBOLD,
} from "../../utils/typography";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgba(25, 118, 210, 0.12)",
    color: "black",
    padding: "7px 5px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: FS_BODY,
    padding: "0 5px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  minHeight: 200,
  transform: "translate(-50%, -50%)",
  minWidth: 460,
  // floats over page content — stays opaque, see SURFACE_OVERLAY in utils/colors
  bgcolor: SURFACE_OVERLAY,
  // borderRadius: 5,
  boxShadow: 24,
  p: 4,
};

const ZOHO = window.ZOHO;

export default function HistoricalData({
  orgId,
  zohoApiKey,
  dataCenterUrl,
  zapiKey,
  connectionUrl,
}) {
  const [loading, setLoading] = useState(false);
  const [handleSubmitLoading, setHandleSubmitLoading] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [cronList, setCronList] = useState([]);
  const [individualCron, setIndividualCron] = useState({});
  const [fetchedBlocks, setFetchedBlocks] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState();
  const [customViews, setCustomViews] = useState([]);
  const [selectedViews, setSelectedViews] = useState();
  const [customViewFetchLoading, setCustomViewFetchLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const handleOpen = (module) => {
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = (type, data) => {
    setIndividualCron({ type: type, data: data });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setIndividualCron({});
    setOpenModal(false);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const visibleRows = React.useMemo(
    () => cronList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [cronList, page, rowsPerPage]
  );

  useEffect(() => {
    if (!selectedModule) {
      return;
    }
    setCustomViewFetchLoading(true);
    ZOHO.CRM.META.getCustomViews({ Entity: selectedModule }).then(function (
      data
    ) {
      setCustomViews(data?.custom_views);
      setCustomViewFetchLoading(false);
    });
  }, [selectedModule]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const filteredData = await getModuleSettings({
          orgId,
          apiKey: zohoApiKey,
        });
        setFetchedBlocks(filteredData);

        // Dedicated v3 store. A job's EXISTENCE is its "In Progress" status;
        // this tab lists every job except the recurring `sync` ones —
        // including unknown/malformed rows, so they stay deletable (§11).
        const jobs = await listCronJobs({ orgId, apiKey: zohoApiKey });

        setPage(0);
        setRowsPerPage(10);
        setCronList(historicalJobs(jobs));

        // A module with a live backfill can't start another one — legacy
        // (untyped) backfill rows count too.
        const runningBackfills = jobs
          .filter(isBackfillJob)
          .map((job) => (job?.body ?? job?.req_body)?.moduleName);

        setAvailableModules(
          (filteredData || [])
            .map((item) => item.module_name)
            .filter((m) => !runningBackfills.includes(m))
        );
      } catch (error) {
        // Subscription-gated endpoints surface their message (§12.10);
        // anything else just leaves the tab empty rather than stuck loading.
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toggle]);

  const handleSubmit = async (module, cv) => {
    let blockData = fetchedBlocks.find((el) => el.module_name === module);

    try {
      setHandleSubmitLoading(true);
      // Backfill mode (§8.2): __backfillmoduledata on crm/v7, execute_in 1,
      // cv_id + pageNo in body, connectionUrl in body for the worker's fast
      // path. buildSyncJobOrg owns the name/connname/params rules.
      const job = buildSyncJobOrg({
        orgId,
        apiKey: zohoApiKey,
        dataCenterUrl,
        zapiKey,
        functionName: DELUGE_FUNCTIONS.BACKFILL,
        moduleName: blockData.module_name,
        type: JOB_TYPES.BACKFILL,
        connectionUrl,
        extraBody: {
          cv_id: { name: cv.display_value, id: cv.id },
          pageNo: 0,
        },
      });
      await createCronJob({ orgId, apiKey: zohoApiKey, job });

      handleClose();
      setToggle(!toggle);
    } catch (error) {
    } finally {
      setHandleSubmitLoading(false);
    }
  };

  const handleCronUpdate = async () => {
    try {
      setHandleSubmitLoading(true);
      // v3 has no Stopped state — a job's existence IS its status, so both
      // "stop" and "delete" are the same DELETE (§8.2).
      await deleteCronJob({
        orgId,
        apiKey: zohoApiKey,
        jobId: cronJobId(individualCron?.data),
      });
      handleCloseModal();
      setToggle(!toggle);
    } catch (error) {
    } finally {
      setHandleSubmitLoading(false);
    }
  };

  //   return <h2>hello </h2>;

  if (loading) {
    return (
      <Backdrop
        sx={{ color: "primary.main", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        Loading
      </Backdrop>
    );
  } else {
    return (
      <Box sx={{ m: 5, mt: 1 }}>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box>
              <Typography
                sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE, my: 1, mb: 3 }}
              >
                Please Select Modules and Custom View
              </Typography>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={availableModules}
                getOptionLabel={(option) => option}
                onChange={(_, data) => {
                  if (data) {
                    setSelectedModule(data);
                  } else {
                    setSelectedModule();
                    setCustomViews([]);
                    setSelectedViews();
                  }
                }}
                size="small"
                sx={{ width: 300, mb: 2 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Modules"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                loading={customViewFetchLoading}
                options={customViews}
                size="small"
                getOptionLabel={(option) => option?.display_value}
                onChange={(_, data) => {
                  if (data) {
                    setSelectedViews(data);
                  } else {
                    setSelectedViews();
                  }
                }}
                sx={{ width: 300 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Custom Views"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  sx={{ mr: 2, width: 120 }}
                  size="small"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  disabled={!selectedViews?.id || handleSubmitLoading}
                  onClick={() => handleSubmit(selectedModule, selectedViews)}
                  variant="contained"
                  sx={{ width: 120 }}
                >
                  {handleSubmitLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      loading
                      <CircularProgress
                        sx={{ color: "white", ml: 1 }}
                        size={17}
                      />{" "}
                    </Box>
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          {" "}
          <Button
            onClick={handleOpen}
            variant="contained"
            size="medium"
          >
            <AddIcon sx={{ mr: "5px", fontSize: "16px" }} />
            Bulk Process
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">Created Date</StyledTableCell>
                <StyledTableCell align="left">Module Name</StyledTableCell>
                <StyledTableCell align="left">Custom View Name</StyledTableCell>
                <StyledTableCell align="center">Status</StyledTableCell>
                {/* <StyledTableCell align="center">Comment</StyledTableCell> */}
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cronList.length > 0 ? (
                visibleRows.map((row, index) => {
                  // In the v3 store a job only exists while it is running, so
                  // a missing status means "In Progress", not "unknown".
                  const status = row?.status || "In Progress";
                  return (
                  <StyledTableRow key={index}>
                    <StyledTableCell align="left">
                      {row?.created_at
                        ? dayjs(row?.created_at).format("MM-DD-YYYY HH:mm")
                        : ""}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row?.body?.moduleName || "—"}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {row?.body?.cv_id?.name || "—"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {" "}
                      {status === "Completed" && (
                        <Chip
                          sx={{
                            fontWeight: FW_MEDIUM,
                            width: 95,
                            height: 25,
                            bgcolor: "rgba(102, 187, 106, 0.4)",
                            color: "#036e07",
                          }}
                          label={status}
                        />
                      )}
                      {status === "In Progress" && (
                        <Chip
                          sx={{
                            fontWeight: FW_MEDIUM,
                            width: 95,
                            height: 25,
                            bgcolor: "rgba(255, 249, 196, 0.8)",
                            color: "black",
                          }}
                          label={status}
                        />
                      )}
                      {!["In Progress", "Completed"].includes(status) && (
                        <Chip
                          sx={{
                            fontWeight: FW_MEDIUM,
                            width: 95,
                            height: 25,
                            bgcolor: "rgba(211, 47, 47, 0.4)",
                            color: "black",
                          }}
                          label={status}
                        />
                      )}
                    </StyledTableCell>
                    {/* <StyledTableCell align="center">
                      {row?.record_count === -1 &&
                        status === "In Progress" &&
                        "Analyzing Data"}
                      {status === "Completed" &&
                        (
                          parseFloat(
                            dayjs(row?.modified_at).diff(
                              dayjs(row?.created_at),
                              "m",
                              true
                            )
                          ) +
                          row.record_count / 50
                        ).toFixed(2)}
                    </StyledTableCell> */}
                    <StyledTableCell align="center">
                      {status === "In Progress" && (
                        <Tooltip title="Stop">
                          <StopCircleIcon
                            onClick={() => handleOpenModal("stop", row)}
                            sx={{
                              cursor: "pointer",
                              color: "rgba(237, 108, 2, 1)",
                            }}
                          />
                        </Tooltip>
                      )}
                      {status !== "In Progress" && (
                        <Tooltip title="Delete">
                          <DeleteOutlineIcon
                            onClick={() => handleOpenModal("delete", row)}
                            sx={{
                              cursor: "pointer",
                              color: "rgba(211, 47, 47, 1)",
                            }}
                          />
                        </Tooltip>
                      )}
                    </StyledTableCell>
                  </StyledTableRow>
                  );
                })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <Typography sx={{ py: 1, fontWeight: FW_SEMIBOLD }}>
                      {" "}
                      No Data Available
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={cronList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box
              sx={{
                bgcolor: SURFACE_OVERLAY,
                borderRadius: 5,
              }}
            >
              {" "}
              <Typography
                sx={{ mt: 4, fontWeight: FW_SEMIBOLD, fontSize: FS_HEADING }}
              >
                Do you want to{" "}
                {individualCron.type === "stop" ? "stop" : "delete"} this log?
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  ml: "1rem",
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                }}
              >
                <Button
                  sx={{ width: 120 }}
                  variant="outlined"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <Button
                  disabled={handleSubmitLoading}
                  sx={{ width: 120 }}
                  variant="contained"
                  onClick={handleCronUpdate}
                >
                  {handleSubmitLoading ? (
                    <CircularProgress
                      sx={{ color: "white", ml: 1 }}
                      size={16}
                    />
                  ) : (
                    "Proceed"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  }
}
